import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircleIcon, ExclamationTriangleIcon, KeyIcon } from '@heroicons/react/24/outline';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';

interface Estado {
  configured: boolean;
  enabled: boolean;
  tokenLast4: string;
  wabaId: string;
  phoneNumberId: string;
}

/**
 * WhatsApp directo por Meta: el administrador pega acá el token permanente de
 * la app propia de ROCAM. El servidor lo guarda y no lo devuelve nunca (solo
 * informa si está cargado y sus últimos 4 caracteres), por eso el campo
 * siempre arranca vacío. Con el interruptor activo, los avisos de texto y las
 * plantillas salen directo a Meta sin pasar por Kapso.
 */
const MetaDirectoCard: React.FC = () => {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [sinAcceso, setSinAcceso] = useState(false);
  const [token, setToken] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState<{ ok: boolean; texto: string } | null>(null);

  const cargar = async () => {
    try {
      const r = await axios.get('/api/whatsapp/meta-direct');
      setEstado(r.data.status);
      setWabaId(r.data.status?.wabaId || '');
    } catch (error: any) {
      if (error?.response?.status === 403) setSinAcceso(true);
    }
  };

  useEffect(() => { cargar(); }, []);

  if (sinAcceso || !estado) return null;

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await axios.post('/api/whatsapp/meta-direct', { accessToken: token.trim() || undefined, wabaId: wabaId.trim() });
      setEstado(r.data.status);
      setToken('');
      setPrueba(null);
      toast.success(token.trim() ? 'Token guardado. Probá la conexión antes de activar.' : 'Datos guardados');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    setProbando(true);
    try {
      const r = await axios.post('/api/whatsapp/meta-direct/test');
      setPrueba({ ok: true, texto: `Meta respondió: ${r.data.name || 'sin nombre'} · ${r.data.number || ''} · calidad ${r.data.quality || 's/d'}` });
    } catch (error: any) {
      setPrueba({ ok: false, texto: error?.response?.data?.error || 'Meta rechazó el token' });
    } finally {
      setProbando(false);
    }
  };

  const alternar = async (enabled: boolean) => {
    try {
      const r = await axios.post('/api/whatsapp/meta-direct', { enabled });
      setEstado(r.data.status);
      toast.success(enabled ? 'Los avisos ahora salen directo por Meta' : 'Los avisos vuelven a salir por Kapso');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'No se pudo cambiar');
    }
  };

  // Se puede activar si la prueba dio bien en esta sesión; apagar, siempre
  const puedeActivar = estado.enabled || (estado.configured && prueba?.ok === true);

  return (
    <div className="mt-6 rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center gap-2">
        <KeyIcon className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-base font-semibold">WhatsApp directo por Meta</h2>
        <Badge variant={estado.enabled ? 'default' : 'secondary'}>{estado.enabled ? 'Envío directo activo' : 'Apagado: sale por Kapso'}</Badge>
        <Badge variant="outline">{estado.configured ? `Token cargado ····${estado.tokenLast4}` : 'Sin token'}</Badge>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Con esto activo, los mensajes de texto y las plantillas (turno confirmado, listo para retirar, recordatorios, reporte diario) salen con la app propia de ROCAM y no cuentan en el cupo de Kapso. Los archivos (PDF, fotos, audios) y el historial del chat siguen pasando por Kapso.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="meta-token" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Token permanente del usuario del sistema</Label>
          <Input id="meta-token" type="password" autoComplete="off" className="h-9" value={token} onChange={(e) => setToken(e.target.value)} placeholder={estado.configured ? 'Pegá uno nuevo solo para reemplazarlo' : 'Pegá el token acá'} />
          <p className="mt-1 text-xs text-muted-foreground">Se guarda en el servidor y no se vuelve a mostrar.</p>
        </div>
        <div>
          <Label htmlFor="meta-waba" className="mb-1.5 block text-xs font-semibold text-muted-foreground">Identificador de la cuenta de WhatsApp (opcional)</Label>
          <Input id="meta-waba" inputMode="numeric" className="h-9" value={wabaId} onChange={(e) => setWabaId(e.target.value.replace(/[^\d]/g, ''))} placeholder="El número largo de la pestaña Resumen" />
          <p className="mt-1 text-xs text-muted-foreground">Número de teléfono en uso: {estado.phoneNumberId || 'sin configurar'}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="button" className="h-9" onClick={guardar} disabled={guardando || (!token.trim() && wabaId.trim() === (estado.wabaId || ''))}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
        <Button type="button" variant="outline" className="h-9" onClick={probar} disabled={probando || !estado.configured}>
          {probando ? 'Consultando a Meta…' : 'Probar conexión (no envía nada)'}
        </Button>
      </div>

      {prueba && (
        <div className={`mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${prueba.ok ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-destructive/40 bg-destructive/5'}`}>
          {prueba.ok ? <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" /> : <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />}
          <span>{prueba.texto}</span>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-4">
        <div className="text-sm">
          <div className="font-medium">Mandar los avisos directo por Meta</div>
          <div className="text-xs text-muted-foreground">
            {puedeActivar ? 'Se puede volver a Kapso en cualquier momento apagando esto.' : 'Se habilita después de guardar el token y de una prueba de conexión correcta.'}
          </div>
        </div>
        <Switch checked={estado.enabled} disabled={!puedeActivar} onCheckedChange={alternar} aria-label="Mandar los avisos directo por Meta" />
      </div>
    </div>
  );
};

export default MetaDirectoCard;
